export type HardwareLookupRow = {
    id: number;
    label: string;
};

type HardwareAutocompleteOptions = {
    lookupUrl: string;
    matchInput?: HTMLInputElement;
    onSelect?: (item: HardwareLookupRow) => void;
};

const MIN_QUERY_LENGTH = 2;
const SEARCH_DELAY_MS = 250;
const BLUR_DELAY_MS = 150;

export function attachHardwareAutocomplete(input: HTMLInputElement, options: HardwareAutocompleteOptions): () => void {
    const formField = input.closest('.form-field');
    if (!(formField instanceof HTMLElement)) {
        return () => undefined;
    }

    input.autocomplete = 'off';

    const status = document.createElement('div');
    status.className = 'field-help';
    status.hidden = true;

    const results = document.createElement('div');
    results.className = 'results-wrapper';
    results.hidden = true;

    formField.append(status, results);

    let suggestions: HardwareLookupRow[] = [];
    let isLoading = false;
    let isOpen = false;
    let ignoreNextInput = false;
    let matchedItem: HardwareLookupRow | null = null;
    let controller: AbortController | null = null;
    let timeoutId = 0;
    let blurTimeoutId = 0;

    const clearPendingSearch = (): void => {
        controller?.abort();
        controller = null;
        window.clearTimeout(timeoutId);
        timeoutId = 0;
    };

    const clearResults = (): void => {
        suggestions = [];
        isLoading = false;
        clearPendingSearch();
    };

    const setMatchedItem = (item: HardwareLookupRow | null): void => {
        matchedItem = item;

        if (options.matchInput !== undefined) {
            options.matchInput.value = item?.id.toString() ?? '';
        }

        if (item === null) {
            delete input.dataset.hardwareMatchId;
            delete input.dataset.hardwareMatchLabel;
            return;
        }

        input.dataset.hardwareMatchId = item.id.toString();
        input.dataset.hardwareMatchLabel = item.label;
    };

    const getExactMatch = (query: string, items: HardwareLookupRow[]): HardwareLookupRow | null => {
        const normalizedQuery = query.trim().toLowerCase();
        return items.find((item) => item.label.trim().toLowerCase() === normalizedQuery) ?? null;
    };

    const render = (): void => {
        const query = input.value.trim();
        const showSuggestions = isOpen && suggestions.length > 0;
        const hasExactMatch = matchedItem !== null && matchedItem.label.trim().toLowerCase() === query.toLowerCase();
        const showNoMatches = isOpen && !isLoading && query.length >= MIN_QUERY_LENGTH && suggestions.length === 0 && !hasExactMatch;

        status.hidden = !isLoading && !showNoMatches;
        status.textContent = isLoading ? 'Searching…' : showNoMatches ? 'No matches found.' : '';

        results.replaceChildren();
        results.hidden = !showSuggestions;

        if (!showSuggestions) {
            return;
        }

        for (const item of suggestions) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'result result--summary';
            button.addEventListener('mousedown', (event) => {
                event.preventDefault();
                clearResults();
                isOpen = false;
                setMatchedItem(item);
                ignoreNextInput = true;
                input.value = item.label;
                options.onSelect?.(item);
                input.dispatchEvent(new Event('input', { bubbles: true }));
                render();
            });

            const value = document.createElement('span');
            value.className = 'result-values';
            value.textContent = item.label;
            button.append(value);
            results.append(button);
        }
    };

    const requestSuggestions = (): void => {
        clearPendingSearch();

        const query = input.value.trim();
        if (query.length < MIN_QUERY_LENGTH) {
            suggestions = [];
            isLoading = false;
            setMatchedItem(null);
            render();
            return;
        }

        if (matchedItem !== null && matchedItem.label.trim().toLowerCase() !== query.toLowerCase()) {
            setMatchedItem(null);
        }

        controller = new AbortController();
        timeoutId = window.setTimeout(async () => {
            isLoading = true;
            render();

            try {
                const response = await fetch(`${options.lookupUrl}?q=${encodeURIComponent(query)}`, {
                    signal: controller?.signal,
                    headers: {
                        Accept: 'application/json'
                    }
                });

                if (!response.ok) {
                    suggestions = [];
                    return;
                }

                suggestions = await response.json() as HardwareLookupRow[];
                setMatchedItem(getExactMatch(query, suggestions));
            }
            catch (error) {
                if ((error as Error).name !== 'AbortError') {
                    suggestions = [];
                    setMatchedItem(null);
                }
            }
            finally {
                isLoading = false;
                controller = null;
                render();
            }
        }, SEARCH_DELAY_MS);
    };

    const handleFocus = (): void => {
        window.clearTimeout(blurTimeoutId);
        isOpen = true;
        render();
    };

    const handleBlur = (): void => {
        blurTimeoutId = window.setTimeout(() => {
            isOpen = false;
            render();
        }, BLUR_DELAY_MS);
    };

    const handleInput = (): void => {
        if (ignoreNextInput) {
            ignoreNextInput = false;
            return;
        }

        requestSuggestions();
    };

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);
    input.addEventListener('input', handleInput);

    return () => {
        clearResults();
        window.clearTimeout(blurTimeoutId);
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
        input.removeEventListener('input', handleInput);
        status.remove();
        results.remove();
    };
}

export function initializeHardwareAutocompleteInputs(root: ParentNode = document): void {
    const inputs = root.querySelectorAll<HTMLInputElement>('input[data-hardware-autocomplete="true"][data-hardware-lookup-url]');

    for (const input of inputs) {
        if (input.dataset.hardwareAutocompleteInitialized === 'true') {
            continue;
        }

        const lookupUrl = input.dataset.hardwareLookupUrl;
        if (typeof lookupUrl !== 'string' || lookupUrl.length === 0) {
            continue;
        }

        const matchInputId = input.dataset.hardwareMatchInputId;
        const matchInput = typeof matchInputId === 'string' && matchInputId.length > 0
            ? document.getElementById(matchInputId)
            : null;

        attachHardwareAutocomplete(input, {
            lookupUrl,
            matchInput: matchInput instanceof HTMLInputElement ? matchInput : undefined
        });
        input.dataset.hardwareAutocompleteInitialized = 'true';
    }
}
